import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAdminApi, errMsg } from "@/lib/api";

const TYPES = ["APEDA registration", "Phytosanitary certificate", "Lab / residue report", "Certificate", "Other"];

export const UploadDocDialog = ({ onDone }) => {
  const call = useAdminApi();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !docType) return toast.error("Choose a type and a file");
    const fd = new FormData();
    fd.append("title", title);
    fd.append("doc_type", docType);
    fd.append("visibility", visibility);
    fd.append("file", file);
    setBusy(true);
    try {
      await call({ method: "post", url: "/admin/documents", data: fd });
      toast.success("Document uploaded");
      setOpen(false);
      setTitle(""); setDocType(""); setFile(null); setVisibility("private");
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-2 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white px-5 py-2.5 text-sm transition-colors" data-testid="doc-upload-open-btn">
          <Upload className="h-4 w-4" /> Upload document
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="doc-upload-form">
          <div className="space-y-2"><Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2} placeholder="e.g. Phyto cert - Banana Oct 2026" data-testid="doc-title-input" /></div>
          <div className="space-y-2"><Label>Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Who can see it</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="doc-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private" data-testid="doc-visibility-private">Private: only via 7-day link</SelectItem>
                <SelectItem value="public" data-testid="doc-visibility-public">Public: listed on Quality page</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>File (PDF, JPG or PNG, max 10 MB)</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" /></div>
          <button type="submit" disabled={busy} data-testid="doc-upload-submit-btn"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-900 text-white px-5 py-2.5 text-sm disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Upload
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
